import { Link } from 'react-router';
import { AppSheet } from '@/components/ui/app-sheet';
import { Button } from '@/components/ui/button';
import { RelativeTime } from '@/components/RelativeTime';
import { useAuth } from '@/hooks/useAuth';
import { useConfirm } from '@/hooks/useConfirm';
import { SessionFlows, VPN_FLOWS } from '@/features/vpnFlows';
import type { VpnSession } from '../api';
import { aliasLabel, endReasonLabel, formatBytes, sessionState, userLabel } from '../format';
import { useEndVpnSession } from '../hooks';
import { useVpnPermissions } from '../permissions';
import { SessionStateBadge } from './Badges';
import { Facts } from './Facts';

/** May the viewer end this session: their own, or anyone's with `vpn:update`. */
function useMayEnd(session: VpnSession): { may: boolean; why?: string } {
  const { user } = useAuth();
  const { canUpdateVpn } = useVpnPermissions();
  if (session.endedAt) return { may: false, why: 'The session has ended' };
  if (session.userId === user?.id || canUpdateVpn) return { may: true };
  return { may: false, why: "Ending someone else's session takes vpn:update" };
}

export function EndSessionButton({ session, size = 'sm' }: { session: VpnSession; size?: 'sm' | 'default' }) {
  const { may, why } = useMayEnd(session);
  const end = useEndVpnSession();
  const { confirm, confirmDialog } = useConfirm();
  const { user } = useAuth();
  if (session.endedAt) return null;

  const onEnd = async () => {
    const own = session.userId === user?.id;
    const ok = await confirm({
      title: 'End this session?',
      message: own
        ? 'Your tunnel to this gateway closes now.'
        : `${userLabel(session)}'s tunnel to ${session.gatewayName ?? 'the gateway'} closes now.`,
      isDanger: true,
      confirmText: 'End session',
    });
    if (ok) end.mutate(session.id);
  };

  // In a table row, a click here (or in the confirmation, whose events bubble
  // through its portal) mustn't also open the row.
  return (
    <span onClick={(e) => { e.stopPropagation(); }}>
      <Button
        size={size}
        variant="outline"
        disabled={!may || end.isPending}
        title={may ? undefined : why}
        onClick={() => void onEnd()}
      >
        End
      </Button>
      {confirmDialog}
    </span>
  );
}

export function SessionSheet({
  session,
  onOpenChange,
}: {
  session: VpnSession | null;
  onOpenChange: (open: boolean) => void;
}) {
  const { canReadSessions } = useVpnPermissions();
  if (!session) return null;
  const state = sessionState(session);

  return (
    <AppSheet
      open
      onOpenChange={onOpenChange}
      size="xl"
      title={
        <span className="flex items-center gap-2">
          {userLabel(session)} at {session.gatewayName ?? 'a gateway'} <SessionStateBadge session={session} />
        </span>
      }
      description={session.clientDeviceName ?? undefined}
    >
      <AppSheet.Body className="space-y-6">
        <Facts
          items={[
            ['User', session.userEmail && session.userName ? `${session.userName} (${session.userEmail})` : userLabel(session)],
            ['Device', session.clientDeviceName],
            ['App', session.appVersion ? `Witina VPN ${session.appVersion}` : null],
            ['Aliases', (session.ipv4Aliases ?? []).map(aliasLabel).join(', ') || null],
            [
              'Gateway',
              <Link key="gw" className="underline" to={`/devices/${session.gatewayId}?tab=vpn`}>
                {session.gatewayName ?? session.gatewayId}
              </Link>,
            ],
            ['Routes', session.allowedPrefixes.join(', ') || '—'],
            ['Requested', <RelativeTime key="rq" value={session.requestedAt} />],
            ['Connected', <RelativeTime key="st" value={session.startedAt} fallback="Not yet" />],
            state === 'ended'
              ? ['Ended', <span key="en"><RelativeTime value={session.endedAt} /> · {endReasonLabel(session.endReason)}</span>]
              : ['Expires', <RelativeTime key="ex" value={session.expiresAt} />],
            ['Client endpoint', session.endpoint],
            ['Last handshake', <RelativeTime key="hs" value={session.lastHandshakeAt} fallback="—" />],
            ['From the client', formatBytes(session.bytesFromClient)],
            ['To the client', formatBytes(session.bytesToClient)],
            ...(session.gatewayRefused ? [['Refused by the gateway', session.gatewayRefused] as const] : []),
          ]}
        />
        {VPN_FLOWS && canReadSessions ? (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Connections</h3>
            <SessionFlows sessionId={session.id} />
          </section>
        ) : null}
      </AppSheet.Body>
      {session.endedAt ? null : (
        <AppSheet.Footer>
          <EndSessionButton session={session} size="default" />
        </AppSheet.Footer>
      )}
    </AppSheet>
  );
}
